import { Component, Inject, LOCALE_ID } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { ApiService, changePasswordAPI } from 'src/app/api/api.service';
import { dashboardRoute } from 'src/app/route-names';
import { ApiAboutService } from 'src/app/Services/api-about.service';
import { DialogService, DialogType } from 'src/app/Services/dialog.service';
import { ElectorDataService } from 'src/app/Services/elector-data.service';
import { SessionService } from 'src/app/Services/session.service';
import { pwRegex } from 'src/app/verify-voter/password.component';

@Component({
    selector: 'app-change-password',
    templateUrl: './change-password.component.html',
    styleUrl: './change-password.component.scss',
    standalone: false
})
export class ChangePasswordComponent {
  public displaySpinner = false;
  dialogText?: string;
  dialogTitle?: string;
  hide0: boolean = true;
  hide1: boolean = true;
  hide2: boolean = true;
  passwordForm: FormGroup;
  passwordRequirements = {
    hasNumber: false,
    hasUpperCase: false,
    hasLowerCase: false,
    hasSpecialChar: false,
    isValidLength: false,
  };
  
  constructor(private _formBuilder: FormBuilder,
    readonly session: SessionService,
    readonly api: ApiService,
    readonly router: Router,
    private readonly userService: ElectorDataService,
    private readonly dialog: MatDialog,
    private dialogService: DialogService,
    private readonly aboutService: ApiAboutService,
    @Inject(LOCALE_ID) readonly locale: string) {
    this.passwordForm = this._formBuilder.group({
      currentPassword: new FormControl<string>('', [Validators.required]),
      password1: new FormControl<string>('', [Validators.required, Validators.pattern(pwRegex)]),
      password2: new FormControl<string>('', [Validators.required, Validators.pattern(pwRegex)])
    }, { validators: this.confirmPasswordValidator });

    const passwordControl = this.passwordForm.get('password1');
    if (passwordControl) {
      passwordControl.valueChanges.subscribe((value: string) => {
        this.validatePassword(value);
      });
    } else {
      console.error('Password control not found');
    }
  }

  private validatePassword(password: string): void {
    this.passwordRequirements.hasNumber = /\d/.test(password);
    this.passwordRequirements.hasUpperCase = /[A-Z]/.test(password);
    this.passwordRequirements.hasLowerCase = /[a-z]/.test(password);
    this.passwordRequirements.hasSpecialChar = /[^a-zA-Z\d]/.test(password);
    this.passwordRequirements.isValidLength = (password.length >= 12) && (password.length <= 1024);
  }

  onSubmit(): boolean {
    if(this.passwordForm.value.currentPassword === this.passwordForm.value.password1 ) {
      this.dialogTitle = $localize`Same Password`;
      this.dialogText = $localize`Please choose a different new password.`;
      this.dialogService.openDialog(DialogType.ALERT, this.dialogTitle, this.dialogText);
      return false;
    }
    this.displaySpinner = true;
    let apiRequest = {
      voter_id: this.userService.userAuthData()?.voter_id,
      currentPassword: this.passwordForm.value.currentPassword,
      newPassword: this.passwordForm.value.password1,
    }
    this.api.postWithHandshakeCheck<{message: string}>(changePasswordAPI, apiRequest).subscribe({
      next: (response) => {
        this.displaySpinner = false;
        this.dialogTitle = $localize`Success!`;
        this.dialogText = $localize`You have successfully changed your password.`;
        this.dialogService.openDialog(DialogType.SUCCESS, this.dialogTitle, this.dialogText);
        setTimeout(() => {
          this.dialog.closeAll();
          this.router.navigate(dashboardRoute);
        }, 2000);
      },
      error: (err) => {
        this.displaySpinner = false; 
        if(err.error.error_type === 'UNKNOWN_USER'){
          this.dialogTitle = $localize`Information not found.`;
          this.dialogText = $localize`Unknown voter. Please close all other tabs with this voter portal and log in again.`;
          this.dialogService.openDialog(DialogType.ALERT, this.dialogTitle, this.dialogText);
        } else if (err.error.error_type === 'BAD_REQUEST') {
          this.dialogTitle = $localize`Bad Request.`;
          this.dialogText = $localize`Please try again or use the "Contact Support" link for assistance.`;
          this.dialogService.openDialog(DialogType.ERROR, this.dialogTitle, this.dialogText);
        } else if(err.error.error_type === 'ACCOUNT_LOCKED') {
          this.dialogTitle = $localize`Locked Account`;
          this.dialogText = $localize`Your account has been locked. Please consult the Help menu for assistance.`;
          this.dialogService.openDialog(DialogType.ERROR, this.dialogTitle, this.dialogText);
          this.session.endSession();
        } else if(err.error.error_type === 'SAME_PASSWORD') {
          this.dialogTitle = $localize`Error: Similar Password`;
          this.dialogText = $localize`Your new password is too similar to the old password or does not meet minimum password requirements. Please choose a different new password.`;
          this.dialogService.openDialog(DialogType.ALERT, this.dialogTitle, this.dialogText);
        } else if(err.error.error_type === 'INVALID_IDENTITY') {
          this.dialogTitle = $localize`Information not found.`;
          this.dialogText = $localize`Unknown voter. Please close all other tabs with this voter portal and log in again.`;
          this.dialogService.openDialog(DialogType.ALERT, this.dialogTitle, this.dialogText);
        } else if (err.error.error_type === 'ELECTION_CLOSED') {
          this.dialogTitle = $localize`Election Closed.`;
          this.dialogText = $localize`The election was open from ${this.aboutService.startTime} to ${this.aboutService.endTime}.`;
          this.dialogService.openDialog(DialogType.ERROR, this.dialogTitle, this.dialogText);
        } else if ((err.error_type !== undefined && (err.error_type === 'DECODER_STATE_LOST' || err.error_type === 'INVALID_MTE_DECODING')) ||
        (err.error !== undefined && (err.error.error_type === 'DECODER_STATE_LOST' || err.error.error_type === 'INVALID_MTE_DECODING' ))) {
          this.dialogText = $localize`We could not process your request. Please log out, log in again, and try again.`;
          this.dialogTitle = $localize`Error.`
          this.dialogService.openDialog(DialogType.ALERT, this.dialogTitle, this.dialogText);
        } else {
          this.dialogTitle = $localize`Error`;
          this.dialogText = $localize`Your password could not be changed. Please try again.`;
          this.dialogService.openDialog(DialogType.ALERT, this.dialogTitle, this.dialogText);
        }
        return false;
      }
    })

    return true;
  }

  confirmPasswordValidator: ValidatorFn = (
    control: AbstractControl
  ): ValidationErrors | null => {
    return control.value.password1 === control.value.password2
      ? null
      : { PasswordNoMatch: true };
  };

  cancel(){
    this.router.navigate(dashboardRoute);
  }
}
